import { useEffect, useState } from "react";
import "./ThirdContactCreditInfo.css";
import usePaginator from "../../../theme/Component/usePaginator";
import useDataBean from "../../../../core/services/dataBean/useDataBean";
import { _CreditAccountvdos } from "../../../../core/services/dataBean/EntityCatalogVdos";
import { _single } from "../../../../core/services/dataBean/EntityCatalog";
import { toast } from "react-toastify";
import { BasicButton } from "../../../theme/Component/BasicButton";
import { Alert } from "../../../theme/Component/Alert";
import { MASK_DATE, MASK_MONEY, MASK_TEXT } from "../../../theme/Component/BasicTable";
import { BasicPage } from "../../../theme/Component/BasicPage";
import { AccountEditor } from "../../../editor/AccountEditor";
import { DetailsThirdContactCreditInfo } from "./DetailsThirdContactCreditInfo/DetailsThirdContactCreditInfo";

interface FilterForm {
  Year: number;
  Month: number;
  State: number | null;
  IdAccount: number;
}

export const ThirdContactCreditInfo = () => {
  const { instance, bean, setBean, lista, setLista } = useDataBean(_CreditAccountvdos);
  const paginador = usePaginator();

  const [filters, setFilters] = useState<Partial<FilterForm>>({
    Month: _single.getCurrentMonth(),
    Year: _single.getCurrenYear(),
    State: 724,
  });

  const [flippedIndex, setFlippedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (filters.IdAccount) {
      getLista();
    } else {
      setLista([]);
      setBean({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.IdAccount, filters.State, filters.Month, filters.Year, paginador.page]);

  const getLista = () => {
    if (!filters.IdAccount) {
      toast.error("Usuario no válido");
      return;
    }

    const props: any = { IDAccount: filters.IdAccount };

    if (filters.State) props.State = filters.State;
    if (filters.Month) props.Month = filters.Month;
    if (filters.Year) props.Year = filters.Year;

    instance
      .getCarteraRender(paginador.page, 15, props)
      .then((resp) => {
        setLista(resp.List || []);
        setBean({});
        paginador.setPages(resp.NumberOfPages || 1);
      })
      .catch((err) => toast.error(err));
  };

  const estados: Record<
    number,
    {
      label: string;
      color: string;
    }
  > = {
    725: { label: "Cancelado", color: "bg-danger" },
    724: { label: "Vigente", color: "bg-primary" },
    723: { label: "Aprobado", color: "bg-success" },
  };

  const getEstado = (id: number) => estados[id] ?? { label: "No encontrado", color: "" };

  const handleSearchClick = () => {
    if (!filters.IdAccount) {
      toast.error("Usuario no válido");
      return;
    }
    paginador.setPage(1);
    getLista();
  };

  return (
    <BasicPage titulo="Consulta Tercero">
      {/* Filtros */}
      <div className="row mb-4">
        <div className="col-md-4">
          <AccountEditor
            idAccount={filters.IdAccount}
            label="Tercero"
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                IdAccount: e.IDAccount,
              }))
            }
          />
        </div>

        <div className="col-md-3">
          <label>Estado</label>
          <select
            value={filters.State ?? ""}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : null;
              setFilters((prev) => ({ ...prev, State: value }));
            }}
            className="form-control"
          >
            <option value="">...</option>
            <option value="725">Cancelado</option>
            <option value="724">Vigente</option>
            <option value="723">Aprobado</option>
          </select>
        </div>

        <div className="col-md-3">
          <BasicButton
            eventClick={handleSearchClick}
            useMargin
            icon="ri-search-line"
          >
            Buscar
          </BasicButton>
        </div>
      </div>

      {/* Paginación */}
      <div className="row">
        <div className="col-md-4 d-flex justify-content-between align-items-center mb-3">
          <button
            className="btn btn-outline-primary btn-sm"
            disabled={paginador.page <= 1}
            onClick={() => paginador.back()}
          >
            Anterior
          </button>

          <span>
            Página <strong>{paginador.page}</strong> de{" "}
            <strong>{paginador.pages}</strong>
          </span>

          <button
            className="btn btn-outline-primary btn-sm"
            disabled={paginador.page >= paginador.pages}
            onClick={() => paginador.next()}
          >
            Siguiente
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="row">
        <div className="col-md-12 tcci-cards-container">
          {lista.length === 0 ? (
            <div className="text-center w-100">
              <Alert clase="info">No hay créditos disponibles</Alert>
            </div>
          ) : (
            lista.map((item: any, index: number) => {
              const props = item.DataBeanProperties;
              const estado = getEstado(props.State);

              const isSelected = bean?.CreditNumber === props.CreditNumber;
              const isFlipped = flippedIndex === index;

              return (
                <div
                  key={props.CreditNumber ?? index}
                  className={[
                    "tcci-card",
                    isFlipped ? "tcci-card--flipped tcci-card--selected" : "",
                    bean?.CreditNumber && !isSelected ? "tcci-card--dimmed" : "",
                  ]
                    .join(" ")
                    .trim()}
                  onClick={() => {
                    setBean(props);
                    setFlippedIndex(isFlipped ? null : index);
                  }}
                >
                  <div className="tcci-card-inner">
                    {/* FRONT */}
                    <div className="tcci-card-front">
                      <h6 className="tcci-card-title">
                        Crédito #{props.CreditNumber}
                      </h6>

                      <div className="tcci-row">
                        <span className="tcci-label">Estado:</span>
                        <span className={`tcci-value ${estado.color}`}>
                          {estado.label}
                        </span>
                      </div>

                      <div className="tcci-row">
                        <span className="tcci-label">Inicio:</span>
                        <span
                          className="tcci-value"
                          dangerouslySetInnerHTML={{
                            __html: _single.formatFieldMask(
                              props.FromDate,
                              MASK_DATE
                            ),
                          }}
                        />
                      </div>

                      <div className="tcci-row">
                        <span className="tcci-label">Fin:</span>
                        <span
                          className="tcci-value"
                          dangerouslySetInnerHTML={{
                            __html: _single.formatFieldMask(
                              props.UptoDate,
                              MASK_DATE
                            ),
                          }}
                        />
                      </div>

                      <div className="tcci-row">
                        <span className="tcci-label">Monto:</span>
                        <span
                          className="tcci-value"
                          dangerouslySetInnerHTML={{
                            __html: _single.formatFieldMask(
                              props.AmountCredit,
                              MASK_MONEY
                            ),
                          }}
                        />
                      </div>

                      <div className="tcci-row">
                        <span className="tcci-label">Saldo:</span>
                        <span
                          className="tcci-value"
                          dangerouslySetInnerHTML={{
                            __html: _single.formatFieldMask(
                              props.CurrentCapitalLeft,
                              MASK_MONEY
                            ),
                          }}
                        />
                      </div>
                    </div>

                    {/* BACK */}
                    <div className="tcci-card-back">
                      <h6 className="tcci-card-title">
                        Crédito #{props.CreditNumber}
                      </h6>
                      <h6 className="tcci-card-title">Detalles adicionales</h6>

                      <div className="tcci-row">
                        <span className="tcci-label">Caja Nominadora:</span>
                        <span
                          className="tcci-value"
                          dangerouslySetInnerHTML={{
                            __html: _single.formatFieldMask(
                              props.CajaNominadora,
                              MASK_TEXT
                            ),
                          }}
                        />
                      </div>

                      <div className="tcci-row">
                        <span className="tcci-label">Tasa de Mora:</span>
                        <span className="tcci-value">
                          {props.RateValueMora} %
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {bean.CreditNumber && (
          <>
            {/* Detalle inferior */}
            <div className="col-12">
              <hr />
              <h5>Detalles del Crédito {bean.CreditNumber}</h5>
            </div>

            <div className="col-md-12">
              <DetailsThirdContactCreditInfo
                _idAccount={filters.IdAccount}
                Credit={bean}
              />
            </div>
          </>
        )}
      </div>
    </BasicPage>
  );
};
